import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PlayArrow, Pause, Compare, VolumeUp, Mic, TrendingUp } from '@mui/icons-material';
import { useReading } from '@hooks/useReading';
import { useTranslation } from 'react-i18next';
import type { ReadingAttempt, AudioComparison as AudioComparisonType } from '@services/reading/types';
import WaveformDisplay from './WaveformDisplay';

interface AudioComparisonProps {
    attempt: ReadingAttempt;
    onClose?: () => void;
}

const AudioComparison: React.FC<AudioComparisonProps> = ({ attempt, onClose }) => {
    const { t } = useTranslation();
    const { readingState } = useReading();
    const [comparison, setComparison] = useState<AudioComparisonType | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPlayingUser, setIsPlayingUser] = useState(false);
    const [isPlayingReference, setIsPlayingReference] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userAudioDuration, setUserAudioDuration] = useState(0);
    const [referenceAudioDuration, setReferenceAudioDuration] = useState(0);
    const [userCurrentTime, setUserCurrentTime] = useState(0);
    const [referenceCurrentTime, setReferenceCurrentTime] = useState(0);
    const [volume, setVolume] = useState(0.8);

    const userAudioRef = useRef<HTMLAudioElement>(null);
    const referenceAudioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        loadComparison();
    }, [attempt.id]);

    const loadComparison = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Import the service - this would need to be available
            const { default: readingService } = await import('@services/reading/readingService');
            const comparisonData = await readingService.compareAudio(attempt.id);
            setComparison(comparisonData);
        } catch (err) {
            setError('Failed to load audio comparison');
            console.error('Audio comparison error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const playUserAudio = () => {
        if (userAudioRef.current) {
            if (isPlayingUser) {
                userAudioRef.current.pause();
                setIsPlayingUser(false);
            } else {
                userAudioRef.current.play();
                setIsPlayingUser(true);
            }
        }
    };

    const playReferenceAudio = () => {
        if (referenceAudioRef.current && comparison?.reference_audio_url) {
            if (isPlayingReference) {
                referenceAudioRef.current.pause();
                setIsPlayingReference(false);
            } else {
                referenceAudioRef.current.play();
                setIsPlayingReference(true);
            }
        }
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const getScoreLabel = (score: number) => {
        if (score >= 90) return t('studentReading.speechAnalysis.scores.excellent');
        if (score >= 80) return t('studentReading.speechAnalysis.scores.veryGood');
        if (score >= 70) return t('studentReading.speechAnalysis.scores.good');
        if (score >= 60) return t('studentReading.speechAnalysis.scores.fair');
        if (score >= 50) return t('studentReading.speechAnalysis.scores.needsImprovement');
        return t('studentReading.speechAnalysis.scores.poor');
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Analyzing audio comparison...
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={loadComparison} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                        <Compare />
                        Audio Comparison Analysis
                    </Typography>
                    {onClose && (
                        <Button variant="outlined" onClick={onClose}>
                            Close
                        </Button>
                    )}
                </Box>

                <Typography variant="body2" color="text.secondary" mb={3}>
                    Compare your pronunciation with the reference audio to identify areas for improvement.
                </Typography>

                {/* Overall Comparison Score */}
                {comparison && (
                    <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        <CardContent>
                            <Box textAlign="center">
                                <Typography variant="h3" fontWeight="bold">
                                    {Math.round(comparison.similarity_score)}%
                                </Typography>
                                <Typography variant="h6">{getScoreLabel(comparison.similarity_score)}</Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={comparison.similarity_score}
                                    sx={{
                                        mt: 1,
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(255,255,255,0.3)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: 'white',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Audio Players */}
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, border: '2px solid', borderColor: 'primary.main' }}>
                            <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                                <Mic />
                                Your Recording
                            </Typography>

                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <IconButton onClick={playUserAudio} color="primary" size="large">
                                    {isPlayingUser ? <Pause /> : <PlayArrow />}
                                </IconButton>
                                <Box flex={1}>
                                    <Typography variant="body2">Duration: {attempt.audio_duration}s</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Chip
                                    label={`Pronunciation: ${Math.round(attempt.pronunciation_score || 0)}%`}
                                    color={getScoreColor(attempt.pronunciation_score || 0)}
                                    size="small"
                                />
                                <Chip
                                    label={`Fluency: ${Math.round(attempt.fluency_score || 0)}%`}
                                    color={getScoreColor(attempt.fluency_score || 0)}
                                    size="small"
                                />
                            </Box>

                            <audio
                                ref={userAudioRef}
                                src={attempt.audio_url}
                                onLoadedMetadata={() => {
                                    if (userAudioRef.current) {
                                        setUserAudioDuration(userAudioRef.current.duration);
                                    }
                                }}
                                onTimeUpdate={() => {
                                    if (userAudioRef.current) {
                                        setUserCurrentTime(userAudioRef.current.currentTime);
                                    }
                                }}
                                onEnded={() => setIsPlayingUser(false)}
                                style={{ display: 'none' }}
                            />
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, border: '2px solid', borderColor: 'success.main' }}>
                            <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                                <VolumeUp />
                                Reference Audio
                            </Typography>

                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <IconButton
                                    onClick={playReferenceAudio}
                                    color="success"
                                    size="large"
                                    disabled={!comparison?.reference_audio_url}
                                >
                                    {isPlayingReference ? <Pause /> : <PlayArrow />}
                                </IconButton>
                                <Box flex={1}>
                                    <Typography variant="body2">
                                        {comparison?.reference_audio_url ? 'Professional Recording' : 'Not Available'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Chip label="Native Speaker" color="success" size="small" />

                            {comparison?.reference_audio_url && (
                                <audio
                                    ref={referenceAudioRef}
                                    src={comparison.reference_audio_url}
                                    onLoadedMetadata={() => {
                                        if (referenceAudioRef.current) {
                                            setReferenceAudioDuration(referenceAudioRef.current.duration);
                                        }
                                    }}
                                    onTimeUpdate={() => {
                                        if (referenceAudioRef.current) {
                                            setReferenceCurrentTime(referenceAudioRef.current.currentTime);
                                        }
                                    }}
                                    onEnded={() => setIsPlayingReference(false)}
                                    style={{ display: 'none' }}
                                />
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Detailed Analysis */}
                {comparison && (
                    <Box>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                            <TrendingUp />
                            Detailed Analysis
                        </Typography>

                        <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="h5" color="primary.main" fontWeight="bold">
                                            {Math.round(comparison.pronunciation_accuracy)}%
                                        </Typography>
                                        <Typography variant="body2">Pronunciation</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="h5" color="secondary.main" fontWeight="bold">
                                            {Math.round(comparison.rhythm_similarity)}%
                                        </Typography>
                                        <Typography variant="body2">Rhythm</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="h5" color="info.main" fontWeight="bold">
                                            {Math.round(comparison.intonation_similarity)}%
                                        </Typography>
                                        <Typography variant="body2">Intonation</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="h5" color="warning.main" fontWeight="bold">
                                            {Math.round(comparison.speed_similarity)}%
                                        </Typography>
                                        <Typography variant="body2">Speaking Speed</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Improvement Suggestions */}
                        {comparison.suggestions && comparison.suggestions.length > 0 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Improvement Suggestions
                                </Typography>
                                {comparison.suggestions.map((suggestion, index) => (
                                    <Alert key={index} severity="info" sx={{ mb: 1 }}>
                                        {suggestion}
                                    </Alert>
                                ))}
                            </Box>
                        )}

                        {/* Word-level Analysis */}
                        {comparison.word_analysis && (
                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom>
                                    Word-by-word Analysis
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {comparison.word_analysis.map((word, index) => (
                                        <Tooltip key={index} title={`Accuracy: ${Math.round(word.accuracy)}%`}>
                                            <Chip
                                                label={word.word}
                                                color={getScoreColor(word.accuracy)}
                                                variant={word.accuracy < 70 ? 'filled' : 'outlined'}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>

            {/* Waveform Comparison */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Waveform Visualization
                </Typography>
                <WaveformDisplay isRecording={false} attempt={attempt} showComparison={true} />
            </Paper>
        </Box>
    );
};

export default AudioComparison;
